import FightResultPopup from "@/components/FightSystem/FightResultPopup.vue";
import { ENABLE_SIEGE_SYSTEM_TOASTS } from "@/config/constants";
import useEmitter, { EmitterEvents, TileLabelAction } from "@/hooks/useEmitter";
import useGameTicker from "@/hooks/useGameTicker";
import useSocketService, { EmitStores } from "@/hooks/useSocketService";
import GameError, { GameErrorType } from "@/libs/GameError";
import { PopupType, usePopupStore } from "@/store/usePopupStore";
import useResourcesStore from "@/store/useResourcesStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useSiegeSystemStore, {
	SiegeSystemResult,
} from "@/store/useSiegeSystemStore";
import { type Rewards } from "@/types";
import type {
	Reward,
	TileCoordinates,
	TileCoordinatesWithValue,
} from "@/types/tile";
import {
	type ArmorOrAttackNumber,
	type ArmorOrAttackObject,
	type ArmorOrAttackValue,
	auraBuffTypes,
	type Buff,
	BuffType,
	selfBuffTypes,
	type TileConfigItem,
	type TileIndex,
	TileLabelSkin,
} from "@/types/tile";
import { SkinTypes } from "@/types/ui";
import { ceilNumber, floorNumber } from "@/utils/number";
import { translate } from "@/utils/translate";
import { markRaw } from "vue";
import {
	FrameName,
	tilerepoFightableEnemiesFrameNames,
	tilerepoFightableMainEnemiesFrameNames,
	tilerepoFriendlyMilitaryUnitsFrameNames,
	tilerepoFriendlySupplyUnitsFrameNames,
	tilerepoFriendlyUnitsFrameNames,
} from "./entities/config";
import { getBuildingConfig } from "./entities/config/buildings";
import { ResourceType } from "./entities/config/resources";
import { getSalvageableConfig } from "./entities/config/salvageables";
import { TileVisibilityType } from "./entities/config/terrain";
import TileEntity from "./entities/TileEntity";
import type TileRepository from "./TileRepository";

type Buffs = {
	armor: ArmorOrAttackValue;
	attack: ArmorOrAttackValue;
	_inEffect: Array<BuffType>;
};

export const EMPTY_SIEGE_COMPONENTS: ArmorOrAttackObject = {
	melee: 0,
	ranged: 0,
	siege: 0,
};

export const FRIENDLY_ATTACK_CLICK_DAMAGE_MULTIPLIER = 5;

export const ENEMY_ATTACK_CLICK_DAMAGE_MULTIPLIER = 2.5;

export const OFFLINE_ONE_FRIENDLY_ATTACK_CLICK_PER_TICK_COUNT = 30; // 1 AC za 30 tickov

export const FAMINE_HEALTH_LOSS_PER_TICK_PERCENTAGE = 0.002; // 0.2% za tick

class SiegeSystemRepository {
	private readonly emitter: ReturnType<typeof useEmitter>;

	private readonly ticker: ReturnType<typeof useGameTicker>;

	private readonly siegeSystemStore: ReturnType<typeof useSiegeSystemStore>;

	private readonly resourcesStore: ReturnType<typeof useResourcesStore>;

	public constructor(private readonly tilerepo: TileRepository) {
		// console.log("SiegeSystemRepository initialized");

		this.emitter = useEmitter();
		this.ticker = useGameTicker();
		this.siegeSystemStore = useSiegeSystemStore();
		this.resourcesStore = useResourcesStore();

		this.ticker.addHandler("1:siege-system", this.onTick.bind(this));
	}

	public setMainEnemyTileIndexByTileIndex(tileIndex: TileIndex | null): void {
		// if (this.siegeSystemStore.mainEnemyTileIndex) return;
		if (tileIndex === this.siegeSystemStore.mainEnemyTileIndex) return;

		if (tileIndex === null) {
			this.siegeSystemStore.setMainEnemyTileIndex(null);
			return;
		}

		this.startSiege(tileIndex);
	}

	public setCanEnemyAttackGuard(value: boolean): void {
		this.siegeSystemStore.setCanEnemyAttackGuard(value);
	}

	public isFriendlySiegeSystemUnitByFrameName(frameName: FrameName): boolean {
		return tilerepoFriendlyUnitsFrameNames.has(frameName);
	}

	public isFriendlySiegeSystemSupplyUnitByFrameName(
		frameName: FrameName,
	): boolean {
		return tilerepoFriendlySupplyUnitsFrameNames.has(frameName);
	}

	public isInActiveSiegeSystemAreaByTileIndex(tileIndex: TileIndex): boolean {
		if (!this.isActive) return false;
		return this.allActiveSiegeSystemTileIndexes.has(tileIndex);
	}

	private get allActiveSiegeSystemTileIndexes(): Set<TileIndex> {
		const tileIndexes = new Set<TileIndex>();
		if (!this.isActive) return tileIndexes;

		const entity = this.tilerepo.getTileEntityByTileIndex(
			this.siegeSystemStore.mainEnemyTileIndex!,
		);
		if (!entity?.range) return tileIndexes;

		const { range } = entity;

		for (let x = entity.x - range; x <= entity.x + range; x++) {
			for (let y = entity.y - range; y <= entity.y + range; y++) {
				const tileIndex = this.tilerepo.getTileIndexByTileCoordinates({
					x,
					y,
				});
				tileIndexes.add(tileIndex);
			}
		}

		return tileIndexes;
	}

	public get allFriendlySiegeSystemUnitsTileIndexes(): Set<TileIndex> {
		const tileIndexes = new Set<TileIndex>();

		const allBuildings = this.tilerepo.getAllBuildingTileIndexes();
		if (!allBuildings.size)
			throw new GameError(
				GameErrorType.FE_ERROR,
				"SiegeSystemRepository: allBuildings is empty",
			);

		for (const frameName of tilerepoFriendlyUnitsFrameNames) {
			const buildingTileIndexes = allBuildings.get(frameName);
			if (!buildingTileIndexes?.size) continue;

			for (const tileIndex of buildingTileIndexes) {
				if (
					this.siegeSystemStore.mainEnemyTileIndex &&
					!this.isInActiveSiegeSystemAreaByTileIndex(tileIndex)
				)
					continue;
				tileIndexes.add(tileIndex);
			}
		}

		return tileIndexes;
	}

	public get allFriendlySiegeSystemUnitsWithoutSupplyUnitsTileIndexes(): Set<TileIndex> {
		const tileIndexes = new Set<TileIndex>();

		const allBuildings = this.tilerepo.getAllBuildingTileIndexes();
		if (!allBuildings.size)
			throw new GameError(
				GameErrorType.FE_ERROR,
				"SiegeSystemRepository: allBuildings is empty",
			);

		for (const frameName of tilerepoFriendlyUnitsFrameNames) {
			if (tilerepoFriendlySupplyUnitsFrameNames.has(frameName)) continue;

			const buildingTileIndexes = allBuildings.get(frameName);
			if (!buildingTileIndexes?.size) continue;

			for (const tileIndex of buildingTileIndexes) {
				if (
					this.siegeSystemStore.mainEnemyTileIndex &&
					!this.isInActiveSiegeSystemAreaByTileIndex(tileIndex)
				)
					continue;
				tileIndexes.add(tileIndex);
			}
		}

		return tileIndexes;
	}

	public get allEnemySiegeSystemUnitsTileIndexes(): Set<TileIndex> {
		const tileIndexes = new Set<TileIndex>();

		const allBuildings = this.tilerepo.getAllBuildingTileIndexes();
		if (!allBuildings.size)
			throw new GameError(
				GameErrorType.FE_ERROR,
				"SiegeSystemRepository: allBuildings is empty",
			);

		for (const frameName of tilerepoFightableEnemiesFrameNames) {
			const buildingTileIndexes = allBuildings.get(frameName);
			if (!buildingTileIndexes?.size) continue;

			for (const tileIndex of buildingTileIndexes) {
				if (
					this.siegeSystemStore.mainEnemyTileIndex &&
					!this.isInActiveSiegeSystemAreaByTileIndex(tileIndex)
				)
					continue;
				tileIndexes.add(tileIndex);
			}
		}

		return tileIndexes;
	}

	public get unlockLevel(): number {
		return (
			getBuildingConfig(FrameName.SUPPLY_CART).requirements.get(
				ResourceType.LEVEL,
			) ?? Number.MAX_SAFE_INTEGER
		);
	}

	public get isUnlocked(): boolean {
		const { mainLevel } = useSettlementsStore();
		return mainLevel >= this.unlockLevel;
	}

	public get isActive(): boolean {
		return this.isUnlocked && this.isSiegeInProgress;
	}

	public get canDeployFriendlySupplyUnits(): boolean {
		return this.isActive;
	}

	public get canDeployFriendlyAttackUnits(): boolean {
		return this.isActive && this.supplyLimit > 0;
	}

	public get isSupplyUnitDeployed(): boolean {
		if (!this.isActive) return false;
		if (!this.allFriendlySiegeSystemUnitsTileIndexes.size) return false;

		return Array.from(this.allFriendlySiegeSystemUnitsTileIndexes).some(
			(tileIndex: TileIndex): boolean => {
				const [, , tileId2] =
					this.tilerepo.getTileIdsByTileIndex(tileIndex);
				if (!tileId2) return false;

				const frameName2 = this.tilerepo.getFrameNameByTileId(tileId2);
				if (!frameName2) return false;

				return tilerepoFriendlySupplyUnitsFrameNames.has(frameName2);
			},
		);
	}

	public get supplyLimitMax(): number {
		if (!this.allFriendlySiegeSystemUnitsTileIndexes.size) return 0;

		return Array.from(this.allFriendlySiegeSystemUnitsTileIndexes).reduce(
			(acc: number, tileIndex: TileIndex): number => {
				const entity =
					this.tilerepo.getTileEntityByTileIndex(tileIndex);
				if (!entity) return acc;

				const unitSupplyLimit =
					entity.siegeAttributes?.supplyLimit ?? 0;

				return acc + (unitSupplyLimit > 0 ? unitSupplyLimit : 0);
			},
			0,
		);
	}

	public get supplyLimit(): number {
		if (!this.allFriendlySiegeSystemUnitsTileIndexes.size) return 0;

		return Array.from(this.allFriendlySiegeSystemUnitsTileIndexes).reduce(
			(acc: number, tileIndex: TileIndex): number => {
				const entity =
					this.tilerepo.getTileEntityByTileIndex(tileIndex);
				if (!entity) return acc;

				const unitSupplyLimit =
					entity.siegeAttributes?.supplyLimit ?? 0;
				return acc + unitSupplyLimit;
			},
			0,
		);
	}

	public get mainEnemyTileIndex(): TileIndex | null {
		return this.siegeSystemStore.mainEnemyTileIndex;
	}

	public getCurrentHpByTileIndex(tileIndex: TileIndex): number | null {
		return this.siegeSystemStore.units?.[`${tileIndex}`]?.hp ?? null;
	}

	public getMaxHpByTileIndex(tileIndex: TileIndex): number | null {
		const [, , tileId2] = this.tilerepo.getTileIdsByTileIndex(tileIndex);
		if (!tileId2) return null;

		const frameName2 = this.tilerepo.getFrameNameByTileId(tileId2);
		if (!frameName2) return null;

		const isFriendly = tilerepoFriendlyUnitsFrameNames.has(frameName2);
		const isEnemy = tilerepoFightableEnemiesFrameNames.has(frameName2);
		if (!isFriendly && !isEnemy) return null;

		return (
			(isFriendly ? getBuildingConfig : getSalvageableConfig)(frameName2)
				.siegeAttributes?.maxHp ?? null
		);
	}

	private getAttributesByTileIndex(
		tileIndex: TileIndex,
	): TileConfigItem["siegeAttributes"] | null {
		const [, , tileId2] = this.tilerepo.getTileIdsByTileIndex(tileIndex);
		if (!tileId2) return null;

		const frameName2 = this.tilerepo.getFrameNameByTileId(tileId2);
		if (!frameName2) return null;

		const isFriendly = tilerepoFriendlyUnitsFrameNames.has(frameName2);
		const isEnemy = tilerepoFightableEnemiesFrameNames.has(frameName2);
		if (!isFriendly && !isEnemy) return null;

		const config = TileEntity.getEntityConfigByFrameName(frameName2);
		return config.siegeAttributes ?? null;
	}

	public getArmorByTileIndex(
		tileIndex: TileIndex,
	): ArmorOrAttackValue | null {
		const attributes = this.getAttributesByTileIndex(tileIndex);
		if (!attributes) return null;

		const { armor } = attributes;
		return armor;
	}

	public getAttackByTileIndex(
		tileIndex: TileIndex,
	): ArmorOrAttackValue | null {
		const attributes = this.getAttributesByTileIndex(tileIndex);
		if (!attributes) return null;

		const { attack } = attributes;
		return attack;
	}

	public getGrantBuffsByTileIndex(tileIndex: TileIndex): Array<Buff> | null {
		const attributes = this.getAttributesByTileIndex(tileIndex);
		if (!attributes) return null;

		const { grantBuffs } = attributes;
		return grantBuffs ?? null;
	}

	public get attackClickCount(): number {
		const { attackClicks } = this.siegeSystemStore;
		return this.capNegativeToZero(attackClicks ?? 0);
	}

	private get enemyUnitToAttack(): TileIndex | null {
		if (!this.siegeSystemStore.siegeTargetTileIndexes.length) return null;
		return this.siegeSystemStore.siegeTargetTileIndexes[0];
	}

	public onTick(): void {
		if (!this.isActive || !this.isSiegeInProgress) return;
		const mainEnemyTileIndex = this.mainEnemyTileIndex;
		const mainEnemyFrameName =
			mainEnemyTileIndex === null
				? undefined
				: this.tilerepo.getTileEntityByTileIndex(mainEnemyTileIndex)
						?.frameName;
		if (
			!mainEnemyFrameName ||
			!tilerepoFightableMainEnemiesFrameNames.has(mainEnemyFrameName)
		) {
			this.siegeSystemStore.reset();
		}

		if (this.resourcesStore[ResourceType.TICKS_TOTAL] % 6 === 0) {
			this.increaseAttackClicks();
		}

		if (this.isFamineActive) {
			const { allFriendlySiegeSystemUnitsWithoutSupplyUnitsTileIndexes } =
				this;
			for (const targetTileIndex of allFriendlySiegeSystemUnitsWithoutSupplyUnitsTileIndexes) {
				const targetHp = this.getCurrentHpByTileIndex(targetTileIndex);
				if (targetHp === null) continue;

				const damage = floorNumber(
					targetHp * FAMINE_HEALTH_LOSS_PER_TICK_PERCENTAGE,
				);
				const newTargetHp = this.capNegativeToZero(targetHp - damage);

				this.siegeSystemStore.setUnit(targetTileIndex, {
					hp: newTargetHp,
				});

				const coordinates =
					this.tilerepo.getTileCoordinatesByTileIndex(
						targetTileIndex,
					);
				if (newTargetHp <= 0) {
					this.tilerepo.setTileByTileCoordinates(
						coordinates,
						FrameName.NOT_SET,
					);
					this.unitDefeated(
						coordinates,
						TileLabelSkin.FIGHT_SYSTEM_DEFEATED_FRIENDLY,
					);
					this.emitter.emit(
						EmitterEvents.PhaserRemoveProgressBar,
						coordinates,
					);

					// if (ENABLE_SIEGE_SYSTEM_TOASTS)
					// 	this.emitter.emit(EmitterEvents.PhaserNeutralMessage, {
					// 		x,
					// 		y,
					// 		text: translate("ui.unit_destroyed"),
					// 		topCorrection: 0,
					// 		ignoreBounds: true,
					// 	});
				}

				// if (ENABLE_SIEGE_SYSTEM_TOASTS)
				// 	this.emitter.emit(EmitterEvents.PhaserNeutralMessage, {
				// 		x,
				// 		y,
				// 		text: `-${damage}`,
				// 		topCorrection: 0,
				// 		ignoreBounds: true,
				// 	});
				if (
					!this
						.allFriendlySiegeSystemUnitsWithoutSupplyUnitsTileIndexes
						.size
				) {
					this.finishSiege(SiegeSystemResult.DEFEAT);
					return;
				}
			}
		}

		// const multiplier =
		// 	1 + this.siegeSystemStore.offlineBoostersAvailableForUse;
		// this.siegeSystemStore.decreaseOfflineBoosters(
		// 	this.siegeSystemStore.offlineBoostersAvailableForUse,
		// );

		this.performSiege(/* multiplier */);
		this.allEnemySiegeSystemUnitsTileIndexes.forEach(tileIndex => {
			const { x, y } =
				this.tilerepo.getTileCoordinatesByTileIndex(tileIndex);
			this.emitter.emit(EmitterEvents.PhaserTileUpdateProgressBar, {
				x,
				y,
			});
		});
		this.allFriendlySiegeSystemUnitsTileIndexes.forEach(tileIndex => {
			const { x, y } =
				this.tilerepo.getTileCoordinatesByTileIndex(tileIndex);
			this.emitter.emit(EmitterEvents.PhaserTileUpdateProgressBar, {
				x,
				y,
			});
		});
	}

	public get siegeTargetCount(): number {
		return this.siegeSystemStore.siegeTargetTileIndexes.length;
	}

	public get siegeTargetCountLimit(): number {
		// apply upgrades
		return 1;
	}

	public isWholeEnemyTerritoryRevealedByTileIndex(
		tileIndex: TileIndex,
	): boolean {
		const mainEnemyTileIndex =
			this.tilerepo.getBlockedByEnemiesMainEnemyTileIndexByTileIndex(
				tileIndex,
			);
		if (!mainEnemyTileIndex) return false;

		const entity = this.tilerepo.getTileEntityByTileIndex(
			mainEnemyTileIndex!,
		);
		if (!entity) return false;

		const neighbours =
			this.tilerepo.getNeighbourTileIndexesByTileCoordinates(
				{
					x: entity.x,
					y: entity.y,
				},
				entity.range ?? 0,
				true,
				new Set<TileVisibilityType>([TileVisibilityType.EXPLORED]),
			);
		return neighbours.length === ((entity.range ?? 0) * 2 + 1) ** 2;
	}

	public canPerformFriendlyActionByTileIndex(
		tileIndex: TileIndex,
		isAttackClick?: boolean,
	): boolean {
		if (!this.isActive) return false;
		if (isAttackClick && this.attackClickCount <= 0) return false;

		const frameName = this.tilerepo.getFrameNameByTileId(
			this.tilerepo.getTileIdsByTileIndex(tileIndex)[2],
		);
		if (!frameName || !tilerepoFightableEnemiesFrameNames.has(frameName))
			return false;

		let hasFriendlyUnitNearby = false;
		const { x, y } = this.tilerepo.getTileCoordinatesByTileIndex(tileIndex);
		for (let dx = -1; dx <= 1; dx++) {
			for (let dy = -1; dy <= 1; dy++) {
				if (dx === 0 && dy === 0) continue;

				const nx = x + dx;
				const ny = y + dy;
				const ni = this.tilerepo.getTileIndexByTileCoordinates({
					x: nx,
					y: ny,
				});

				const [, , tileId2] = this.tilerepo.getTileIdsByTileIndex(ni);
				if (!tileId2) continue;

				const frameName2 = this.tilerepo.getFrameNameByTileId(tileId2);
				if (
					!frameName2 ||
					!tilerepoFriendlyMilitaryUnitsFrameNames.has(frameName2)
				)
					continue;

				hasFriendlyUnitNearby = true;
				break;
			}
			if (hasFriendlyUnitNearby) break;
		}

		return hasFriendlyUnitNearby;
	}

	public canPerformAttackClickByTileIndex(tileIndex: TileIndex): boolean {
		if (this.attackClickCount <= 0) return false;
		return this.canPerformFriendlyActionByTileIndex(tileIndex, true);
	}

	public canPerformAddSiegeTargetByTileIndex(tileIndex: TileIndex): boolean {
		// if (this.siegeSystemStore.siegeTargetTileIndexes.length >= this.siegeTargetCountLimit) return false;
		return this.canPerformFriendlyActionByTileIndex(tileIndex);
	}

	public canPerformSiegeByTileIndex(tileIndex: TileIndex): boolean {
		return this.canPerformFriendlyActionByTileIndex(tileIndex);
	}

	public performAttackClickByTileIndex(
		targetTileIndex: TileIndex,
	): number | null {
		if (!this.canPerformFriendlyActionByTileIndex(targetTileIndex, true))
			return null;

		const damage = this.getFriendlyDamage(
			targetTileIndex,
			FRIENDLY_ATTACK_CLICK_DAMAGE_MULTIPLIER,
		);
		if (damage === null) return null;

		this.decreaseAttackClicks();

		if (!damage) {
			const { x, y } =
				this.tilerepo.getTileCoordinatesByTileIndex(targetTileIndex);
			if (ENABLE_SIEGE_SYSTEM_TOASTS)
				this.emitter.emit(EmitterEvents.PhaserNeutralMessage, {
					x,
					y,
					text: `-${damage}`,
					topCorrection: 0,
				});

			return null;
		}

		const targetHp =
			this.siegeSystemStore.units?.[`${targetTileIndex}`]?.hp ?? 0;
		const newTargetHp = this.capNegativeToZero(targetHp - damage);
		this.siegeSystemStore.increaseDamageDealt(damage);

		// console.log("SIEGE BY FRIENDLY 2/2", {
		// 	targetTileIndex,
		// 	targetHp,
		// 	newTargetHp,
		// });

		if (newTargetHp <= 0) {
			const { mainEnemyTileIndex } = this.siegeSystemStore;
			if (!mainEnemyTileIndex)
				throw new GameError(
					GameErrorType.SIEGE_SYSTEM_ERROR,
					"SiegeSystemRepository: mainEnemyTileIndex is null",
				);

			const mainEnemyEntity =
				this.tilerepo.getTileEntityByTileIndex(mainEnemyTileIndex);
			if (!mainEnemyEntity)
				throw new GameError(
					GameErrorType.SIEGE_SYSTEM_ERROR,
					"SiegeSystemRepository: mainEnemyEntity is null",
				);

			const mainEnemyRange = mainEnemyEntity.range ?? 0;
			if (!mainEnemyRange)
				throw new GameError(
					GameErrorType.SIEGE_SYSTEM_ERROR,
					"SiegeSystemRepository: mainEnemyRange is 0",
				);

			const isMainEnemyDefeated =
				targetTileIndex === this.siegeSystemStore.mainEnemyTileIndex;

			if (isMainEnemyDefeated) this.updateRewards();

			this.removeSiegeTargetByTileIndex(targetTileIndex);
			// this.siegeSystemStore.setUnit(targetTileIndex, { hp: 0 });
			this.siegeSystemStore.removeUnit(targetTileIndex);
			this.siegeSystemStore.increaseEnemiesDefeated();

			const { x, y } =
				this.tilerepo.getTileCoordinatesByTileIndex(targetTileIndex);
			this.tilerepo.setTileByTileCoordinates({ x, y }, FrameName.NOT_SET);
			this.unitDefeated(
				{ x, y },
				TileLabelSkin.FIGHT_SYSTEM_DEFEATED_ENEMY,
			);

			if (ENABLE_SIEGE_SYSTEM_TOASTS)
				this.emitter.emit(EmitterEvents.PhaserNeutralMessage, {
					x,
					y,
					text: translate("ui.unit_destroyed"),
					topCorrection: 0,
					ignoreBounds: true,
				});

			this.emitter.emit(EmitterEvents.PhaserRemoveProgressBar, { x, y });

			// console.log("SIEGE BY FRIENDLY: Enemy unit defeated", {
			// 	targetTileIndex,
			// 	targetHp,
			// 	newTargetHp,
			// 	emeySize: this.allEnemySiegeSystemUnitsTileIndexes.size,
			// });

			if (
				isMainEnemyDefeated ||
				!this.siegeSystemStore.mainEnemyTileIndex ||
				!this.allEnemySiegeSystemUnitsTileIndexes.size
			)
				this.finishSiege(SiegeSystemResult.VICTORY);

			if (
				isMainEnemyDefeated &&
				this.siegeSystemStore.mainEnemyTileIndex !== null
			)
				this.setMainEnemyTileIndexByTileIndex(null);

			return damage;
		}

		this.siegeSystemStore.setUnit(targetTileIndex, { hp: newTargetHp });

		const { x, y } =
			this.tilerepo.getTileCoordinatesByTileIndex(targetTileIndex);
		if (ENABLE_SIEGE_SYSTEM_TOASTS)
			this.emitter.emit(EmitterEvents.PhaserNeutralMessage, {
				x,
				y,
				text: `-${damage}`,
				topCorrection: 0,
			});

		this.performSiegeByEnemy(ENEMY_ATTACK_CLICK_DAMAGE_MULTIPLIER);
		this.emitter.emit(EmitterEvents.PhaserTileUpdateProgressBar, {
			x,
			y,
		});
		this.allFriendlySiegeSystemUnitsTileIndexes.forEach(tileIndex => {
			const { x, y } =
				this.tilerepo.getTileCoordinatesByTileIndex(tileIndex);
			this.emitter.emit(EmitterEvents.PhaserTileUpdateProgressBar, {
				x,
				y,
			});
		});
		return damage;
	}

	public addSiegeTargetByTileIndex(tileIndex: TileIndex): void {
		if (!this.canPerformAddSiegeTargetByTileIndex(tileIndex)) return;

		// zatial mame len tento stav, zvysne treba potom dorobit
		if (this.siegeTargetCountLimit <= 1) {
			if (
				this.siegeSystemStore.siegeTargetTileIndexes.includes(tileIndex)
			) {
				this.siegeSystemStore.removeSiegeTargetTileIndex(tileIndex);
				return;
			}

			this.siegeSystemStore.setSiegeTargetTileIndexes([tileIndex]);
			this.setMainEnemyTileIndexByTileIndex(tileIndex);
		}
	}

	public removeSiegeTargetByTileIndex(tileIndex: TileIndex): void {
		// if (!this.canPerformAddSiegeTargetByTileIndex(tileIndex)) return;
		this.siegeSystemStore.removeSiegeTargetTileIndex(tileIndex);
	}

	private getFriendlyDamages(targetTileIndex: TileIndex): {
		melee: number;
		ranged: number;
		siege: number;
	} {
		const COMPONENT_KEYS = Object.keys(EMPTY_SIEGE_COMPONENTS) as Array<
			keyof ArmorOrAttackObject
		>;
		const targetArmor: ArmorOrAttackObject = {
			...EMPTY_SIEGE_COMPONENTS,
		};
		const sourceAttack: ArmorOrAttackObject = {
			...EMPTY_SIEGE_COMPONENTS,
		};

		const targetAttributes = this.getAttributesByTileIndex(targetTileIndex);
		if (!targetAttributes)
			return {
				melee: 0,
				ranged: 0,
				siege: 0,
			};

		const { armor: targetArmorBase } = targetAttributes;
		const {
			armor: targetArmorBuffs,
			// _inEffect: targetBuffsInEffect
		} = this.getBuffsByTileIndex(targetTileIndex) ?? {};
		for (const key of COMPONENT_KEYS) {
			targetArmor[key] +=
				(targetArmorBase as ArmorOrAttackObject)[key] ?? 0;
			targetArmor[key] +=
				(targetArmorBuffs as ArmorOrAttackObject)?.[key] ?? 0;
		}

		// console.log("SIEGE BY FRIENDLY -1/1", {
		// 	sourceAttack: { ...sourceAttack },
		// });

		const { allFriendlySiegeSystemUnitsTileIndexes } = this;
		for (const tileIndex of allFriendlySiegeSystemUnitsTileIndexes) {
			const sourceAttributes = this.getAttributesByTileIndex(tileIndex);
			if (!sourceAttributes) continue;

			const { attack: sourceAttackBase } = sourceAttributes;
			const {
				attack: sourceAttackBuffs,
				// _inEffect: sourceBuffsInEffect
			} = this.getBuffsByTileIndex(tileIndex) ?? {};

			// console.log("SIEGE BY FRIENDLY 0/2", {
			// 	tileIndex,
			// 	frameName: this.tilerepo.getFrameNameByTileId(
			// 		this.tilerepo.getTileIdsByTileIndex(tileIndex)[2],
			// 	),
			// 	sourceAttackBase,
			// 	sourceAttackBuffs,
			// 	sourceAttack: { ...sourceAttack },
			// 	sourceAttacknew: sourceAttack,
			// });

			for (const key of COMPONENT_KEYS) {
				sourceAttack[key] +=
					(sourceAttackBase as ArmorOrAttackObject)[key] ?? 0;
				sourceAttack[key] +=
					(sourceAttackBuffs as ArmorOrAttackObject)?.[key] ?? 0;
			}
		}

		return {
			melee: this.capNegativeToZero(
				this.capNegativeToZero(sourceAttack.melee) -
					this.capNegativeToZero(targetArmor.melee),
			),
			ranged: this.capNegativeToZero(
				this.capNegativeToZero(sourceAttack.ranged) -
					this.capNegativeToZero(targetArmor.ranged),
			),
			siege: this.capNegativeToZero(
				this.capNegativeToZero(sourceAttack.siege) -
					this.capNegativeToZero(targetArmor.siege),
			),
		};
	}

	public getFriendlyDamage(
		targetTileIndex: TileIndex,
		multiplier = 1,
	): number | null {
		// console.log("SIEGE BY FRIENDLY 1/2", {
		// 	targetArmor,
		// 	targetArmorBase,
		// 	targetArmorBuffs,
		// 	targetBuffsInEffect,
		// 	sourceAttack,
		// 	targetTileIndex,
		// 	damage,
		// 	multiplier,
		// });
		const damage = this.getFriendlyDamages(targetTileIndex);
		return ceilNumber(
			(damage.melee + damage.ranged + damage.siege) * multiplier,
		);
	}

	public getAllFriendlyDamage(): {
		melee: number;
		ranged: number;
		siege: number;
	} {
		const damage = {
			melee: 0,
			ranged: 0,
			siege: 0,
		};

		this.allFriendlySiegeSystemUnitsTileIndexes.forEach(tileIndex => {
			const targetAttributes = this.getAttributesByTileIndex(tileIndex);
			if (!targetAttributes) return;

			const { attack } = targetAttributes;
			const attackValue =
				typeof attack === "number"
					? { melee: attack, ranged: attack, siege: attack }
					: (attack ?? EMPTY_SIEGE_COMPONENTS);
			const { attack: sourceAttackBuffsRaw } =
				this.getBuffsByTileIndex(tileIndex) ?? {};
			const sourceAttackBuffs =
				typeof sourceAttackBuffsRaw === "number"
					? {
							melee: sourceAttackBuffsRaw,
							ranged: sourceAttackBuffsRaw,
							siege: sourceAttackBuffsRaw,
						}
					: (sourceAttackBuffsRaw ?? EMPTY_SIEGE_COMPONENTS);
			damage.melee += attackValue.melee + sourceAttackBuffs.melee;
			damage.ranged += attackValue.ranged + sourceAttackBuffs.ranged;
			damage.siege += attackValue.siege + sourceAttackBuffs.siege;
		});
		return damage;
	}

	private performSiegeByFriendly(multiplier = 1): void {
		const { enemyUnitToAttack: targetTileIndex } = this;
		if (
			targetTileIndex === null ||
			!this.canPerformSiegeByTileIndex(targetTileIndex)
		)
			return;

		const damage = this.getFriendlyDamage(targetTileIndex, multiplier);
		if (!damage) return;

		const targetHp =
			this.siegeSystemStore.units?.[`${targetTileIndex}`]?.hp ?? 0;
		const newTargetHp = this.capNegativeToZero(targetHp - damage);

		// console.log("SIEGE BY FRIENDLY 2/2", {
		// 	targetTileIndex,
		// 	targetHp,
		// 	newTargetHp,
		// 	multiplier,
		// });

		if (newTargetHp <= 0) {
			if (targetTileIndex === this.siegeSystemStore.mainEnemyTileIndex)
				this.updateRewards();

			this.siegeSystemStore.increaseDamageDealt(damage);
			this.removeSiegeTargetByTileIndex(targetTileIndex);
			// this.siegeSystemStore.setUnit(targetTileIndex, { hp: 0 });
			this.siegeSystemStore.removeUnit(targetTileIndex);
			this.siegeSystemStore.increaseEnemiesDefeated();

			const { x, y } =
				this.tilerepo.getTileCoordinatesByTileIndex(targetTileIndex);
			this.tilerepo.setTileByTileCoordinates({ x, y }, FrameName.NOT_SET); //
			this.unitDefeated(
				{ x, y },
				TileLabelSkin.FIGHT_SYSTEM_DEFEATED_ENEMY,
			);

			if (ENABLE_SIEGE_SYSTEM_TOASTS)
				this.emitter.emit(EmitterEvents.PhaserNeutralMessage, {
					x,
					y,
					text: translate("ui.unit_destroyed"),
					topCorrection: 0,
					ignoreBounds: true,
				});

			this.emitter.emit(EmitterEvents.PhaserRemoveProgressBar, { x, y });

			return;
		}

		this.siegeSystemStore.increaseDamageDealt(damage);
		this.siegeSystemStore.setUnit(targetTileIndex, { hp: newTargetHp });

		const { x, y } =
			this.tilerepo.getTileCoordinatesByTileIndex(targetTileIndex);
		if (ENABLE_SIEGE_SYSTEM_TOASTS)
			this.emitter.emit(EmitterEvents.PhaserNeutralMessage, {
				x,
				y,
				text: `-${damage}`,
				topCorrection: 0,
				ignoreBounds: true,
			});
	}

	private performSiegeByEnemy(multiplier = 1): void {
		if (!this.siegeSystemStore.canEnemyAttackGuard) return;

		const startingTileIndex =
			this.enemyUnitToAttack ??
			this.siegeSystemStore.mainEnemyTileIndex ??
			null;
		if (startingTileIndex === null) return;

		// TODO: early exit if there are only friendly supply units left

		const sourceUnitsThatPerformedAttackTileIndexes = new Set<TileIndex>();
		const targetUnitsDamageToasts = {} as Record<TileIndex, number>;

		const sourceEntity =
			this.tilerepo.getTileEntityByTileIndex(startingTileIndex);
		if (!sourceEntity) return;

		// nula sa zmeni na cele uzemie, nemoze to byt range, lebo to by musela byt unita presne v strede
		const roundRanges = [1, 2, 0] as const;
		for (const roundRange of roundRanges) {
			const { allEnemySiegeSystemUnitsTileIndexes } = this;
			if (
				allEnemySiegeSystemUnitsTileIndexes.size ===
				sourceUnitsThatPerformedAttackTileIndexes.size
			)
				break;

			const isLastRound =
				roundRange === roundRanges[roundRanges.length - 1];
			const targetsUnitsInRange = isLastRound
				? Array.from(
						this
							.allFriendlySiegeSystemUnitsWithoutSupplyUnitsTileIndexes,
					)
				: this.tilerepo
						.getNeighbourTileIndexesByTileCoordinates(
							this.tilerepo.getTileCoordinatesByTileIndex(
								startingTileIndex,
							),
							roundRange,
						)
						.filter((tileIndex: TileIndex): boolean =>
							this.allFriendlySiegeSystemUnitsWithoutSupplyUnitsTileIndexes.has(
								tileIndex,
							),
						);
			const { allEnemySiegeSystemUnitsTileIndexes: sourceTileIndexes } =
				this;
			for (const sourceTileIndex of sourceTileIndexes) {
				const sourceChanceToAttack = Math.random();
				if (sourceChanceToAttack < 0.4 && !isLastRound) continue;

				if (
					sourceUnitsThatPerformedAttackTileIndexes.has(
						sourceTileIndex,
					)
				)
					continue;

				if (!targetsUnitsInRange.length) continue;

				const targetTileIndex =
					targetsUnitsInRange[
						Math.floor(Math.random() * targetsUnitsInRange.length)
					];

				const sourceAttributes =
					this.getAttributesByTileIndex(sourceTileIndex);
				const targetAttributes =
					this.getAttributesByTileIndex(targetTileIndex);
				if (!targetAttributes || !sourceAttributes) continue;

				sourceUnitsThatPerformedAttackTileIndexes.add(sourceTileIndex);

				const {
					attack: sourceAttackBuffs,
					// _inEffect: sourceBuffsInEffect,
				} = this.getBuffsByTileIndex(sourceTileIndex) || {};
				const {
					armor: targetArmorBuffs,
					// _inEffect: targetBuffsInEffect,
				} = this.getBuffsByTileIndex(targetTileIndex) || {};
				const { armor: targetArmorBase } = targetAttributes;
				const { attack: sourceAttackBase } = sourceAttributes;

				// const entries = Object.entries(_sourceAttackBase) as Array<
				// 	[keyof ArmorOrAttackObject, ArmorOrAttackNumber]
				// >;
				// const nonZeroEntry = entries.find(
				// 	([, value]: [
				// 		keyof ArmorOrAttackObject,
				// 		ArmorOrAttackNumber,
				// 	]) => value > 0,
				// ) as
				// 	| [keyof ArmorOrAttackObject, ArmorOrAttackNumber]
				// 	| undefined;
				// const sourceAttackBase = nonZeroEntry
				// 	? ((_sourceAttackBase as ArmorOrAttackObject)[
				// 			nonZeroEntry[0]
				// 		] as ArmorOrAttackNumber)
				// 	: 0;
				// const sourceAttackBuffs = nonZeroEntry
				// 	? ((_sourceAttackBuffs as ArmorOrAttackObject)[
				// 			nonZeroEntry[0]
				// 		] as ArmorOrAttackNumber)
				// 	: 0;

				const damage = floorNumber(
					this.capNegativeToZero(
						((sourceAttackBase as ArmorOrAttackNumber) +
							(sourceAttackBuffs as ArmorOrAttackNumber) -
							((targetArmorBase as ArmorOrAttackNumber) +
								(targetArmorBuffs as ArmorOrAttackNumber))) *
							multiplier,
					),
				);
				const targetHp =
					this.siegeSystemStore.units?.[`${targetTileIndex}`]?.hp ??
					0;
				const newTargetHp = this.capNegativeToZero(targetHp - damage);
				this.siegeSystemStore.increaseDamageTaken(damage);

				// console.log("SIEGE BY ENEMY", {
				// 	startingTileIndex,
				// 	roundRange,
				// 	roundIndex: roundRanges.indexOf(roundRange),
				// 	sourceTileIndex,
				// 	targetTileIndex,
				// 	sourceAttackBase,
				// 	sourceAttackBuffs,
				// 	targetArmorBase,
				// 	targetArmorBuffs,
				// 	sourceBuffsInEffect,
				// 	targetBuffsInEffect,
				// 	targetFrameName: this.tilerepo.getFrameNameByTileId(
				// 		this.tilerepo.getTileIdsByTileIndex(targetTileIndex)[2],
				// 	),
				// 	sourceFrameName: this.tilerepo.getFrameNameByTileId(
				// 		this.tilerepo.getTileIdsByTileIndex(sourceTileIndex)[2],
				// 	),
				// 	targetHp,
				// 	damage,
				// 	newTargetHp,
				// 	targetsUnitsInRange,
				// 	sourceAttributes,
				// 	targetAttributes,
				// 	multiplier,
				// });

				if (newTargetHp <= 0) {
					// this.siegeSystemStore.setUnit(targetTileIndex, { hp: 0 });
					this.siegeSystemStore.removeUnit(targetTileIndex);

					const { x, y } =
						this.tilerepo.getTileCoordinatesByTileIndex(
							targetTileIndex,
						);
					this.tilerepo.setTileByTileCoordinates(
						{ x, y },
						FrameName.NOT_SET,
					);
					this.unitDefeated(
						{ x, y },
						TileLabelSkin.FIGHT_SYSTEM_DEFEATED_FRIENDLY,
					);

					if (ENABLE_SIEGE_SYSTEM_TOASTS)
						this.emitter.emit(EmitterEvents.PhaserNeutralMessage, {
							x,
							y,
							text: translate("ui.unit_destroyed"),
							topCorrection: 0,
							ignoreBounds: true,
						});

					this.emitter.emit(EmitterEvents.PhaserRemoveProgressBar, {
						x,
						y,
					});

					// re-cap available attack clicks
					this.siegeSystemStore.setAttackClicks(
						Math.min(
							this.attackClickCount,
							this.maxAttackClickCount,
						),
					);
					continue;
				}

				this.siegeSystemStore.setUnit(targetTileIndex, {
					hp: newTargetHp,
				});

				targetUnitsDamageToasts[targetTileIndex] =
					(targetUnitsDamageToasts[targetTileIndex] ?? 0) + damage;
			}
		}

		for (const [targetTileIndex, damage] of Object.entries(
			targetUnitsDamageToasts,
		)) {
			const { x, y } =
				this.tilerepo.getTileCoordinatesByTileIndex(+targetTileIndex);
			if (ENABLE_SIEGE_SYSTEM_TOASTS)
				this.emitter.emit(EmitterEvents.PhaserNeutralMessage, {
					x,
					y,
					text: `-${damage}`,
					topCorrection: 0,
					ignoreBounds: true,
				});
		}
	}

	private performSiege(multiplier = 1): void {
		// if (FLAG_ENABLE_TIMERS || 1) console.log("performSiege: start");
		// if (FLAG_ENABLE_TIMERS || 1) console.time("performSiege: end");

		if (!this.siegeSystemStore.canEnemyAttackGuard) return;
		if (!this.isSiegeInProgress) return;

		const { mainEnemyTileIndex } = this.siegeSystemStore;
		if (!mainEnemyTileIndex)
			throw new GameError(
				GameErrorType.SIEGE_SYSTEM_ERROR,
				"SiegeSystemRepository: mainEnemyTileIndex is null",
			);

		const mainEnemyEntity =
			this.tilerepo.getTileEntityByTileIndex(mainEnemyTileIndex);
		if (!mainEnemyEntity)
			throw new GameError(
				GameErrorType.SIEGE_SYSTEM_ERROR,
				"SiegeSystemRepository: mainEnemyEntity is null",
			);

		const mainEnemyRange = mainEnemyEntity.range ?? 0;
		if (!mainEnemyRange)
			throw new GameError(
				GameErrorType.SIEGE_SYSTEM_ERROR,
				"SiegeSystemRepository: mainEnemyRange is 0",
			);

		this.performSiegeByFriendly(multiplier);
		if (!this.allEnemySiegeSystemUnitsTileIndexes.size) {
			this.finishSiege(SiegeSystemResult.VICTORY);
			return;
		}

		this.performSiegeByEnemy(multiplier);
		if (
			!this.allFriendlySiegeSystemUnitsWithoutSupplyUnitsTileIndexes.size
		) {
			this.finishSiege(SiegeSystemResult.DEFEAT);
			return;
		}

		// if (FLAG_ENABLE_TIMERS || 1) console.timeEnd("performSiege: end");
	}

	public resetSiege(): void {
		this.siegeSystemStore.reset();
	}

	private startSiege(tileIndex: TileIndex): void {
		if (this.isSiegeInProgress) return;

		const mainEnemyTileIndex =
			this.tilerepo.getBlockedByEnemiesMainEnemyTileIndexByTileIndex(
				tileIndex,
			);
		if (!mainEnemyTileIndex)
			throw new GameError(
				GameErrorType.SIEGE_SYSTEM_ERROR,
				"SiegeSystemRepository: mainEnemyTileIndex is null",
			);

		const mainEnemyEntity =
			this.tilerepo.getTileEntityByTileIndex(mainEnemyTileIndex);
		if (!mainEnemyEntity)
			throw new GameError(
				GameErrorType.SIEGE_SYSTEM_ERROR,
				"SiegeSystemRepository: mainEnemyEntity is null",
			);

		const mainEnemyRange = mainEnemyEntity.range ?? 0;
		if (!mainEnemyRange)
			throw new GameError(
				GameErrorType.SIEGE_SYSTEM_ERROR,
				"SiegeSystemRepository: mainEnemyRange is 0",
			);

		this.siegeSystemStore.reset();
		this.siegeSystemStore.setMainEnemyTileIndex(mainEnemyTileIndex);
		this.siegeSystemStore.setCurrentBattlefield({
			tileIndex: mainEnemyTileIndex,
			range: mainEnemyRange,
		});
		this.siegeSystemStore.setStartedAt(Date.now());
		this.siegeSystemStore.setEnemiesTotal(
			this.allEnemySiegeSystemUnitsTileIndexes.size,
		);

		this.updateRewards();
		this.updateStoreUnits();
	}

	public finishSiege(result: SiegeSystemResult, isDisband?: boolean): void {
		if (this.siegeResult) return;
		if (
			result === SiegeSystemResult.DEFEAT &&
			this.allFriendlySiegeSystemUnitsTileIndexes.size &&
			(isDisband ||
				!this.siegeSystemStore.canEnemyAttackGuard ||
				this.siegeSystemStore.movingUnitsIndex)
		)
			return;

		const { mainEnemyTileIndex } = this.siegeSystemStore;
		if (!mainEnemyTileIndex)
			throw new GameError(
				GameErrorType.SIEGE_SYSTEM_ERROR,
				"SiegeSystemRepository: mainEnemyTileIndex is null",
			);

		this.siegeSystemStore.setResult(result);
		this.siegeSystemStore.setEndedAt(Date.now());

		this.siegeSystemStore.setMainEnemyTileIndex(null);
		this.siegeSystemStore.setSiegeTargetTileIndexes([]);
		this.siegeSystemStore.setAttackClicks(0);

		// ulozime si rewardy a statistiky, aby sa dali zobrazit v popup-e aj po refreshi
		useSocketService().emitSaveRequest({
			stores: [
				EmitStores.MAP,
				EmitStores.RESOURCES,
				EmitStores.SIEGE_SYSTEM,
			],
		});

		usePopupStore().showPopup({
			type: PopupType.INFO,
			skin: SkinTypes.DARK,
			isPaddingless: true,
			isCentered: true,
			isPriority: true,
			height: "auto",
			width: 23.875,
			component: markRaw(FightResultPopup),
		});
	}

	public clearBattlefield(
		result: SiegeSystemResult | null,
		mainEnemyTileIndex: TileIndex,
		mainEnemyRange: number,
	): void {
		// if (FLAG_ENABLE_TIMERS || 1) console.log("clearBattlefield:start");
		// if (FLAG_ENABLE_TIMERS || 1) console.time("clearBattlefield:end");

		if (!result || !mainEnemyTileIndex || !mainEnemyRange) return;

		const refund: Rewards = {};

		const { x, y } =
			this.tilerepo.getTileCoordinatesByTileIndex(mainEnemyTileIndex);
		for (let dx = -mainEnemyRange; dx <= mainEnemyRange; dx++) {
			for (let dy = -mainEnemyRange; dy <= mainEnemyRange; dy++) {
				const tileIndex = this.tilerepo.getTileIndexByTileCoordinates({
					x: x + dx,
					y: y + dy,
				});
				if (tileIndex === null) continue;

				const [, , tileId2] =
					this.tilerepo.getTileIdsByTileIndex(tileIndex);
				if (!tileId2) continue;

				const frameName2 = this.tilerepo.getFrameNameByTileId(tileId2);
				if (!frameName2) continue;

				if (
					!tilerepoFriendlyUnitsFrameNames.has(frameName2) &&
					!tilerepoFightableEnemiesFrameNames.has(frameName2)
				)
					continue;
				if (
					result === SiegeSystemResult.DEFEAT &&
					tilerepoFightableEnemiesFrameNames.has(frameName2)
				)
					continue;

				if (tilerepoFriendlyUnitsFrameNames.has(frameName2)) {
					const entity =
						this.tilerepo.getTileEntityByTileIndex(tileIndex);
					if (!entity) continue;

					if (entity.friendlyUnitDisbandReward) {
						const rs = useResourcesStore();
						const entries = Object.entries(
							entity.friendlyUnitDisbandReward,
						) as Array<[keyof Reward, Reward[keyof Reward]]>;
						for (const [resourceType, value] of entries) {
							if (!value) continue;

							if (!refund[resourceType]) refund[resourceType] = 0;
							refund[resourceType]! += value;
							rs.increase(resourceType, value, "siege cleanup");
						}
					}
				}

				this.tilerepo.setTileByTileCoordinates(
					{ x: x + dx, y: y + dy },
					FrameName.NOT_SET,
				);

				this.removeDefeatedUnitLabel({ x: x + dx, y: y + dy });
			}
		}

		this.removeDefeatedUnitLabel({ x, y });

		const { defeatedUnits } = this.siegeSystemStore;
		for (const { coordinates } of defeatedUnits) {
			this.removeDefeatedUnitLabel(coordinates);
		}

		useEmitter().emit(EmitterEvents.PhaserTileClick, {
			x,
			y,
			reward: refund,
			randomX: false,
		});

		this.siegeSystemStore.setCurrentBattlefield({});

		this.tilerepo.fixRedTiles({
			forceMainEnemyTileIndex: mainEnemyTileIndex,
			forceMainEnemyRange: mainEnemyRange,
		});

		// if (FLAG_ENABLE_TIMERS || 1) console.timeEnd("clearBattlefield:end");
	}

	public get maxAttackClickCount(): number {
		let maxAttackClickCount = 0;
		const { allFriendlySiegeSystemUnitsTileIndexes: tileIndexes } = this;
		for (const tileIndex of tileIndexes) {
			const attributes = this.getAttributesByTileIndex(tileIndex);
			if (!attributes) continue;
			maxAttackClickCount += attributes.attackClicks ?? 0;
		}
		return maxAttackClickCount;
	}

	// public, lebo v offline income sa to pouziva
	public increaseAttackClicks(amount = 1, doNotCap = false): void {
		this.siegeSystemStore.setAttackClicks(
			!doNotCap
				? Math.min(
						this.attackClickCount + amount,
						this.maxAttackClickCount,
					)
				: this.attackClickCount + amount,
		);
	}

	private decreaseAttackClicks(amount = 1): void {
		this.siegeSystemStore.setAttackClicks(
			this.capNegativeToZero(this.attackClickCount - amount),
		);
	}

	private get isSiegeInProgress(): boolean {
		return this.siegeSystemStore.mainEnemyTileIndex !== null;
	}

	public get duration(): number | null {
		if (!this.siegeSystemStore.stats.startedAt) return null;
		return Math.trunc(
			((this.siegeSystemStore.stats.endedAt ?? Date.now()) -
				this.siegeSystemStore.stats.startedAt) /
				1_000,
		);
	}

	public get damageDealt(): number | null {
		if (!this.siegeSystemStore.stats.startedAt) return null;
		return this.siegeSystemStore.stats.damageDealt ?? 0;
	}

	public get damageTaken(): number | null {
		if (!this.siegeSystemStore.stats.startedAt) return null;
		return this.siegeSystemStore.stats.damageTaken ?? 0;
	}

	public get enemiesDefeated(): number | null {
		if (!this.siegeSystemStore.stats.startedAt) return null;
		return this.siegeSystemStore.stats.enemiesDefeated ?? 0;
	}

	public get enemiesTotal(): number | null {
		if (!this.siegeSystemStore.stats.startedAt) return null;
		return this.siegeSystemStore.stats.enemiesTotal ?? 0;
	}

	public get siegeResult(): SiegeSystemResult | null {
		return this.siegeSystemStore.result;
	}

	public getBuffsByTileIndex(tileIndex: TileIndex): Buffs | null {
		const entity = this.tilerepo.getTileEntityByTileIndex(tileIndex);
		if (!entity) return null;

		const isFriendly = tilerepoFriendlyUnitsFrameNames.has(
			entity.frameName,
		);
		const isEnemy = tilerepoFightableEnemiesFrameNames.has(
			entity.frameName,
		);
		if (!isFriendly && !isEnemy) return null;

		const buffs: Buffs = {
			armor: null!,
			attack: null!,
			_inEffect: new Array<BuffType>(),
		}; // null sa vymeni za spravny zapis, alebo sa vyhlasi exitus

		if (isFriendly) {
			buffs.armor = 0;
			buffs.attack = { ...EMPTY_SIEGE_COMPONENTS };
		}

		if (isEnemy) {
			buffs.armor = { ...EMPTY_SIEGE_COMPONENTS };
			buffs.attack = 0;
		}

		if (buffs.armor === null || buffs.attack === null) return null;

		const battlefieldEntities = [
			...this.allFriendlySiegeSystemUnitsTileIndexes,
			...this.allEnemySiegeSystemUnitsTileIndexes,
		]
			.map((tileIndex: TileIndex) =>
				this.tilerepo.getTileEntityByTileIndex(tileIndex),
			)
			.filter(
				(tileEntity): boolean => tileEntity !== null,
			) as Array<TileEntity>;
		const neighbourEntities = battlefieldEntities.filter(
			({ x, y }: TileEntity): boolean =>
				Math.abs(x - entity.x) <= 1 && Math.abs(y - entity.y) <= 1,
		);

		// self buffs
		for (const grantBuff of entity.siegeAttributes?.grantBuffs ?? []) {
			if (!selfBuffTypes.has(grantBuff.type)) continue;

			switch (grantBuff.type) {
				// pridá X DMG ak susedí s aspoň jednou nepriateľskou jednotkou v rangi 1
				case BuffType.ENGAGEMENT_ATTACK: {
					if (
						neighbourEntities.some((entity: TileEntity): boolean =>
							(isFriendly
								? tilerepoFightableEnemiesFrameNames
								: tilerepoFriendlyUnitsFrameNames
							).has(entity.frameName),
						) &&
						entity.siegeAttributes
					) {
						buffs._inEffect.push(grantBuff.type);

						if (isFriendly) {
							(buffs.attack as ArmorOrAttackObject).melee += (
								entity.siegeAttributes
									.attack as ArmorOrAttackObject
							)?.melee
								? grantBuff.value
								: 0;
							(buffs.attack as ArmorOrAttackObject).ranged += (
								entity.siegeAttributes
									.attack as ArmorOrAttackObject
							)?.ranged
								? grantBuff.value
								: 0;
							(buffs.attack as ArmorOrAttackObject).siege += (
								entity.siegeAttributes
									.attack as ArmorOrAttackObject
							)?.siege
								? grantBuff.value
								: 0;
						}

						if (isEnemy) {
							(buffs.attack as ArmorOrAttackNumber) += (entity
								.siegeAttributes.attack as ArmorOrAttackNumber)
								? grantBuff.value
								: 0;
						}
					}
					break;
				}
				// pridá X DMG za každá nepriateľskú jednotku v rangi 1
				case BuffType.BATTLE_FRENZY_ATTACK: {
					// toto je enemy podla aktualne jednotky, cize pre friendly je to enemy a pre enemy je to friendly
					const enemies = neighbourEntities.filter(
						(entity: TileEntity): boolean =>
							(isFriendly
								? tilerepoFightableEnemiesFrameNames
								: tilerepoFriendlyUnitsFrameNames
							).has(entity.frameName),
					);
					if (enemies.length) {
						buffs._inEffect.push(grantBuff.type);

						if (isFriendly) {
							(buffs.attack as ArmorOrAttackObject).melee += (
								entity.siegeAttributes!
									.attack as ArmorOrAttackObject
							)?.melee
								? grantBuff.value * enemies.length
								: 0;
							(buffs.attack as ArmorOrAttackObject).ranged += (
								entity.siegeAttributes!
									.attack as ArmorOrAttackObject
							)?.ranged
								? grantBuff.value * enemies.length
								: 0;
							(buffs.attack as ArmorOrAttackObject).siege += (
								entity.siegeAttributes!
									.attack as ArmorOrAttackObject
							)?.siege
								? grantBuff.value * enemies.length
								: 0;
						}

						if (isEnemy) {
							(buffs.attack as ArmorOrAttackNumber) += (entity
								.siegeAttributes!.attack as ArmorOrAttackNumber)
								? grantBuff.value * enemies.length
								: 0;
						}
					}
					break;
				}
				// pridá X DMG ak jednotka nesusedí so žiadnou nepriateľskou v rangi 1
				case BuffType.ISOLATION_ATTACK: {
					const enemies = neighbourEntities.filter(
						(entity: TileEntity): boolean =>
							(isFriendly
								? tilerepoFightableEnemiesFrameNames
								: tilerepoFriendlyUnitsFrameNames
							).has(entity.frameName),
					);
					if (!enemies.length) {
						buffs._inEffect.push(grantBuff.type);

						if (isFriendly) {
							(buffs.attack as ArmorOrAttackObject).melee += (
								entity.siegeAttributes!
									.attack as ArmorOrAttackObject
							)?.melee
								? grantBuff.value
								: 0;
							(buffs.attack as ArmorOrAttackObject).ranged += (
								entity.siegeAttributes!
									.attack as ArmorOrAttackObject
							)?.ranged
								? grantBuff.value
								: 0;
							(buffs.attack as ArmorOrAttackObject).siege += (
								entity.siegeAttributes!
									.attack as ArmorOrAttackObject
							)?.siege
								? grantBuff.value
								: 0;
						}

						if (isEnemy) {
							(buffs.attack as ArmorOrAttackNumber) += (entity
								.siegeAttributes!.attack as ArmorOrAttackNumber)
								? grantBuff.value
								: 0;
						}
					}
					break;
				}
			}
		}

		// aura buffs
		for (const battlefieldEntity of neighbourEntities) {
			for (const grantBuff of battlefieldEntity.siegeAttributes
				?.grantBuffs ?? []) {
				if (!auraBuffTypes.has(grantBuff.type)) continue;

				switch (grantBuff.type) {
					// TODO: GD spec missing
					case BuffType.ADD_ARMOR_MELEE: {
						if (
							(isFriendly
								? tilerepoFriendlyUnitsFrameNames
								: tilerepoFightableEnemiesFrameNames
							).has(battlefieldEntity.frameName)
						) {
							buffs._inEffect.push(grantBuff.type);

							if (isFriendly) {
								throw new GameError(
									GameErrorType.SIEGE_SYSTEM_ERROR,
									"ADD_ARMOR_MELEE not implemented for friendly units",
								);
							}

							if (isEnemy) {
								(buffs.armor as ArmorOrAttackObject).melee +=
									grantBuff.value;
							}
						}
						break;
					}
					// TODO: GD spec missing
					case BuffType.ADD_ARMOR_RANGED: {
						if (
							(isFriendly
								? tilerepoFriendlyUnitsFrameNames
								: tilerepoFightableEnemiesFrameNames
							).has(battlefieldEntity.frameName)
						) {
							buffs._inEffect.push(grantBuff.type);

							if (isFriendly) {
								throw new GameError(
									GameErrorType.SIEGE_SYSTEM_ERROR,
									"ADD_ARMOR_RANGED not implemented for friendly units",
								);
							}

							if (isEnemy) {
								(buffs.armor as ArmorOrAttackObject).ranged +=
									grantBuff.value;
							}
						}
						break;
					}
					// pridá všetky typy armorov rovnakým spojeneckých jednotkám
					case BuffType.ADD_ARMOR_SAME: {
						if (battlefieldEntity.frameName === entity.frameName) {
							buffs._inEffect.push(grantBuff.type);

							if (isFriendly) {
								(buffs.armor as ArmorOrAttackNumber) +=
									grantBuff.value;
							}

							if (isEnemy) {
								(buffs.armor as ArmorOrAttackObject).melee +=
									grantBuff.value;
								(buffs.armor as ArmorOrAttackObject).ranged +=
									grantBuff.value;
								(buffs.armor as ArmorOrAttackObject).siege +=
									grantBuff.value;
							}
						}

						break;
					}
					// uberie zo všetkých typov armorov do nepriateľských jednotiek
					case BuffType.LOWER_ARMOR: {
						if (
							(isFriendly
								? tilerepoFightableEnemiesFrameNames
								: tilerepoFriendlyUnitsFrameNames
							).has(battlefieldEntity.frameName)
						) {
							buffs._inEffect.push(grantBuff.type);

							if (isFriendly) {
								(buffs.armor as ArmorOrAttackNumber) -=
									grantBuff.value;
							}

							if (isEnemy) {
								(buffs.armor as ArmorOrAttackObject).melee -=
									grantBuff.value;
								(buffs.armor as ArmorOrAttackObject).ranged -=
									grantBuff.value;
								(buffs.armor as ArmorOrAttackObject).siege -=
									grantBuff.value;
							}
						}

						break;
					}
					// pridá DMG všetkým spojeneckým jednotkám
					case BuffType.ADD_ATTACK: {
						if (
							(isFriendly
								? tilerepoFriendlyUnitsFrameNames
								: tilerepoFightableEnemiesFrameNames
							).has(battlefieldEntity.frameName)
						) {
							buffs._inEffect.push(grantBuff.type);

							if (isFriendly) {
								(buffs.attack as ArmorOrAttackObject).melee += (
									entity.siegeAttributes!
										.attack as ArmorOrAttackObject
								)?.melee
									? grantBuff.value
									: 0;
								(buffs.attack as ArmorOrAttackObject).ranged +=
									(
										entity.siegeAttributes!
											.attack as ArmorOrAttackObject
									)?.ranged
										? grantBuff.value
										: 0;
								(buffs.attack as ArmorOrAttackObject).siege += (
									entity.siegeAttributes!
										.attack as ArmorOrAttackObject
								)?.siege
									? grantBuff.value
									: 0;
							}

							if (isEnemy) {
								(buffs.attack as ArmorOrAttackNumber) +=
									grantBuff.value;
							}
						}

						break;
					}
				}
			}
		}

		return buffs;
	}

	public get isFamineActive(): boolean {
		return (
			this.supplyLimit < 0
			// Toto GDs zatial vypli.
			// || this.resourcesStore[ResourceType.FOOD_DIFF] < 0
		);
	}

	public getRewardsByTileIndex(
		tileIndex: TileIndex,
	): Partial<Record<ResourceType, number>> {
		const mainEnemyTileIndex =
			this.tilerepo.getBlockedByEnemiesMainEnemyTileIndexByTileIndex(
				tileIndex,
			);
		if (!mainEnemyTileIndex) return {};

		const mainEnemyEntity =
			this.tilerepo.getTileEntityByTileIndex(mainEnemyTileIndex);
		if (!mainEnemyEntity?.range) return {};

		const manhattanDistance =
			Math.abs(mainEnemyEntity.x - this.tilerepo.MAP_SIZE / 2) +
			Math.abs(mainEnemyEntity.y - this.tilerepo.MAP_SIZE / 2);
		const explorationPrice =
			150 +
			(1 + manhattanDistance / 1) *
				Math.pow((3 + manhattanDistance) / 4.5, 2.3);
		const battlefieldSize = (mainEnemyEntity.range * 2 + 1) ** 2;

		return {
			[ResourceType.COINS]: ceilNumber(
				explorationPrice * battlefieldSize * 5,
			),
		};
	}

	private updateRewards(): void {
		if (!this.siegeSystemStore.mainEnemyTileIndex) return;
		// if (this.siegeResult !== SiegeSystemResult.VICTORY) return null;

		const rewards = this.getRewardsByTileIndex(
			this.siegeSystemStore.mainEnemyTileIndex,
		);
		this.siegeSystemStore.setRewards(rewards);
	}

	public get rewards(): Partial<Record<ResourceType, number>> | null {
		const { rewards } = this.siegeSystemStore;
		if (!rewards) return null;
		return rewards;
	}

	// Toto si povedali, ze nakoniec nechcu. Samozrejme, ze to uz bolo nakodene...
	/*
	public canBuildFriendlyUnitByFrameName(
		targetFrameName: FrameName,
	): boolean {
		if (!tilerepoFriendlyUnitsFrameNames.has(targetFrameName)) return false;

		const { buildingLimitFrameName } =
			getBuildingConfig(targetFrameName)?.siegeAttributes ?? {};
		if (!buildingLimitFrameName) return false;

		const targetBuildingCount = this.tilerepo.getTileCount2(
			new Set<FrameName>([buildingLimitFrameName]),
		);
		const currentBuildingCount = this.tilerepo.getTileCount2(
			new Set<FrameName>([targetFrameName]),
		);

		return currentBuildingCount < targetBuildingCount;
	}
	*/

	public canBuildFriendlyUnitByTileIndex(
		tileIndex: TileIndex,
		frameName: FrameName,
	): boolean {
		if (!tilerepoFriendlyUnitsFrameNames.has(frameName)) return false;

		const entity = this.tilerepo.createEntityByFrameName(
			frameName,
			this.tilerepo.getTileCoordinatesByTileIndex(tileIndex),
		);
		if (!entity)
			throw new GameError(
				GameErrorType.SIEGE_SYSTEM_ERROR,
				"SiegeSystemRepository: entity is null",
			);

		const { supplyLimit } = this;
		const unitSupplyLimit = entity.siegeAttributes?.supplyLimit ?? 0;
		const isSupplyLimitSatisfied =
			unitSupplyLimit > 0 ? true : supplyLimit + unitSupplyLimit >= 0;

		return (
			isSupplyLimitSatisfied &&
			this.isWholeEnemyTerritoryRevealedByTileIndex(tileIndex) &&
			(!this.siegeSystemStore.mainEnemyTileIndex ||
				this.siegeSystemStore.mainEnemyTileIndex ===
					this.tilerepo.getBlockedByEnemiesMainEnemyTileIndexByTileIndex(
						tileIndex,
					))
		);
	}

	public isSiegeTargetByTileIndex(tileIndex: TileIndex): boolean {
		return this.siegeSystemStore.siegeTargetTileIndexes.includes(tileIndex);
	}

	public canAddSiegeTargetByTileIndex(tileIndex: TileIndex): boolean {
		if (
			this.siegeSystemStore.siegeTargetTileIndexes.length >=
			this.siegeTargetCountLimit
		)
			return false;
		return this.canPerformAddSiegeTargetByTileIndex(tileIndex);
	}

	public updateStoreUnits(): void {
		const { allActiveSiegeSystemTileIndexes } = this;
		for (const tileIndex of allActiveSiegeSystemTileIndexes) {
			if (this.siegeSystemStore.isUnitInStore(tileIndex)) continue;

			const attributes = this.getAttributesByTileIndex(tileIndex);
			if (!attributes) continue;

			this.siegeSystemStore.setUnit(tileIndex, {
				hp: attributes.maxHp ?? 0,
			});
		}

		const {
			allFriendlySiegeSystemUnitsTileIndexes,
			allEnemySiegeSystemUnitsTileIndexes,
		} = this;
		if (
			!allFriendlySiegeSystemUnitsTileIndexes.size &&
			!allEnemySiegeSystemUnitsTileIndexes.size
		)
			return;

		const storeUnitsTileIndexes = Object.keys(
			this.siegeSystemStore.units,
		).map((tileIndex: string): TileIndex => +tileIndex);
		for (const tileIndex of storeUnitsTileIndexes) {
			if (
				allFriendlySiegeSystemUnitsTileIndexes.has(tileIndex) ||
				allEnemySiegeSystemUnitsTileIndexes.has(tileIndex)
			)
				continue;
			this.siegeSystemStore.removeUnit(tileIndex);
		}
	}

	public rerenderAllBattlefieldLabels(): void {
		const entries = this.tilerepo.getAllBuildingTileIndexes().entries();
		for (const [frameName, tileIndexes] of entries) {
			if (!tilerepoFightableMainEnemiesFrameNames.has(frameName))
				continue;

			for (const tileIndex of tileIndexes) {
				if (!this.isWholeEnemyTerritoryRevealedByTileIndex(tileIndex))
					continue;

				this.rerenderBattlefieldLabelByTileIndex(tileIndex);
			}
		}
	}

	public rerenderBattlefieldLabelByTileIndex(tileIndex: TileIndex): void {
		// Ak mame result, tak chceme vyclearovat battlefield.
		if (this.siegeResult) {
			const coordinates =
				this.tilerepo.getTileCoordinatesByTileIndex(tileIndex);
			this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
				coordinates: coordinates,
				skin: TileLabelSkin.FIGHT_SYSTEM_ALL,
				action: TileLabelAction.DELETE_MULTIPLE_SKINS_AT_COORS,
			});
			return;
		}

		// Ak nie je odhalene cele bojisko, tak nechceme este menit label.
		if (!this.isWholeEnemyTerritoryRevealedByTileIndex(tileIndex)) return;

		const mainEnemyTileIndex =
			this.tilerepo.getBlockedByEnemiesMainEnemyTileIndexByTileIndex(
				tileIndex,
			);
		const mainEnemyEntity = mainEnemyTileIndex
			? this.tilerepo.getTileEntityByTileIndex(mainEnemyTileIndex)
			: null;
		if (!mainEnemyEntity?.range) {
			const coordinates =
				this.tilerepo.getTileCoordinatesByTileIndex(tileIndex);
			this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
				coordinates: coordinates,
				skin: TileLabelSkin.FIGHT_SYSTEM_ALL,
				action: TileLabelAction.DELETE_MULTIPLE_SKINS_AT_COORS,
			});
			return;
		}

		const coordinates = {
			x: mainEnemyEntity.x,
			y: mainEnemyEntity.y,
			offsetY: -mainEnemyEntity.range + 0.35, // bulharska konstanta, aby bol label spravne nad bojiskom
		};

		let labelSkin: TileLabelSkin = TileLabelSkin.FIGHT_SYSTEM_1;
		if (
			mainEnemyEntity.index === this.siegeSystemStore.mainEnemyTileIndex
		) {
			labelSkin = TileLabelSkin.FIGHT_SYSTEM_2;

			if (this.isFamineActive) {
				labelSkin = TileLabelSkin.FIGHT_SYSTEM_3;
			}
		}
		// else if (this.siegeResult === SiegeSystemResult.VICTORY) {
		// 	// TODO: Tuto potrebujeme to zobrazit len pre dany battlefield, toto zobrazi vsade.
		// 	// Musim premysliet, ci nezachovam mainEnemyTileIndex.
		// 	labelSkin = TileLabelSkin.FIGHT_SYSTEM_4;
		// }

		this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
			coordinates,
			skin: TileLabelSkin.FIGHT_SYSTEM_ALL,
			action: TileLabelAction.DELETE_MULTIPLE_SKINS_AT_COORS,
		});

		this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
			coordinates,
			skin: labelSkin,
			action: TileLabelAction.CREATE,
		});
	}

	public moveUnit(unit: FrameName, coordinates: TileCoordinates): void {
		if (this.siegeSystemStore.movingUnitsIndex === null) return;

		const unitData =
			this.siegeSystemStore.units[
				`${this.siegeSystemStore.movingUnitsIndex}`
			];
		if (!unitData) {
			this.siegeSystemStore.setMovingUnitsIndex(null);
			return;
		}

		this.tilerepo.setTileByTileCoordinates(coordinates, unit);

		const oldCoordinates = this.tilerepo.getTileCoordinatesByTileIndex(
			this.siegeSystemStore.movingUnitsIndex,
		);
		const newIndex =
			this.tilerepo.getTileIndexByTileCoordinates(coordinates);

		this.siegeSystemStore.setUnit(newIndex, { hp: unitData.hp });
		this.siegeSystemStore.removeUnit(
			this.siegeSystemStore.movingUnitsIndex,
		);
		this.tilerepo.setTileByTileCoordinates(
			oldCoordinates,
			FrameName.NOT_SET,
		);
		this.siegeSystemStore.setMovingUnitsIndex(null);

		const exploredTilesBlockedByEnemies =
			new Set<TileCoordinatesWithValue>();
		exploredTilesBlockedByEnemies.add({
			x: oldCoordinates.x,
			y: oldCoordinates.y,
			value: false,
		});
		this.emitter.emit(
			EmitterEvents.PhaserRenderBlockedByEnemy,
			exploredTilesBlockedByEnemies,
		);
		this.emitter.emit(EmitterEvents.SiegeSystemProgressUpdate, coordinates);
	}

	private unitDefeated(
		coordinates: TileCoordinates,
		skin:
			| TileLabelSkin.FIGHT_SYSTEM_DEFEATED_FRIENDLY
			| TileLabelSkin.FIGHT_SYSTEM_DEFEATED_ENEMY,
	): void {
		this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
			coordinates,
			skin,
			action: TileLabelAction.CREATE,
			scaleToZoom: false,
		});
		const { defeatedUnits } = this.siegeSystemStore;
		this.siegeSystemStore.setDefeatedUnits([
			...defeatedUnits,
			{ coordinates, skin },
		]);
	}

	public removeDefeatedUnitLabel(coordinates: TileCoordinates): void {
		const { defeatedUnits } = this.siegeSystemStore;
		const updatedDefeatedUnits = defeatedUnits.filter(
			unit =>
				unit.coordinates.x !== coordinates.x ||
				unit.coordinates.y !== coordinates.y,
		);
		this.siegeSystemStore.setDefeatedUnits(updatedDefeatedUnits);
		this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
			coordinates,
			skin: TileLabelSkin.FIGHT_SYSTEM_DEFEATED_FRIENDLY, // or any default skin you want to use for removal
			action: TileLabelAction.DELETE,
		});
		this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
			coordinates,
			skin: TileLabelSkin.FIGHT_SYSTEM_DEFEATED_ENEMY, // or any default skin you want to use for removal
			action: TileLabelAction.DELETE,
		});
	}

	public initTileLabels(): void {
		const { defeatedUnits } = this.siegeSystemStore;
		for (const unit of defeatedUnits) {
			this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
				coordinates: unit.coordinates,
				skin: unit.skin,
				action: TileLabelAction.CREATE,
				scaleToZoom: false,
			});
		}
	}

	private capNegativeToZero(value: number): number {
		return Math.max(value, 0);
	}
}

export default SiegeSystemRepository;
